import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  PPE_CATALOGUE,
  parsePpeList,
  serializePpeList,
} from '../constants/riskAssessment';

/**
 * PPE picker — horizontal strip of compact pictograms.
 *
 * Props:
 *  - value: string — JSON-stringified array of catalogue slugs (storage form)
 *  - onChange?(nextJsonString): local-state echo (every click)
 *  - onCommit?(nextJsonString): persist (every click — clicks are atomic)
 *  - disabled: bool
 *  - readOnly: bool — when true, only the selected pictograms render,
 *      each with its label underneath. Used in the library viewer.
 */
export default function PpePicker({
  value,
  onChange,
  onCommit,
  disabled = false,
  readOnly = false,
}) {
  const initialSet = useMemo(() => new Set(parsePpeList(value)), [value]);
  const [selected, setSelected] = useState(initialSet);

  useEffect(() => {
    setSelected(new Set(parsePpeList(value)));
  }, [value]);

  const toggle = useCallback(
    (slug) => {
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(slug)) next.delete(slug);
        else next.add(slug);
        const arr = [...next];
        const str = serializePpeList(arr);
        onChange?.(str);
        onCommit?.(str);
        return next;
      });
    },
    [onChange, onCommit]
  );

  if (readOnly) {
    const items = PPE_CATALOGUE.filter((p) => selected.has(p.slug));
    if (items.length === 0) {
      return (
        <div className="mt-1 text-[11px] italic text-gray-400 dark:text-gray-500">
          No PPE specified.
        </div>
      );
    }
    return (
      <div className="mt-1 flex flex-wrap gap-2">
        {items.map((p) => (
          <span
            key={p.slug}
            title={p.label}
            className="inline-flex flex-col items-center text-[10px] text-gray-600 dark:text-gray-300 w-12"
          >
            <span className="text-lg leading-none">{p.emoji}</span>
            <span className="text-center leading-tight mt-0.5">{p.label}</span>
          </span>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-1 max-md:gap-2">
      {PPE_CATALOGUE.map((p) => {
        const isOn = selected.has(p.slug);
        return (
          <button
            key={p.slug}
            type="button"
            disabled={disabled}
            onClick={() => toggle(p.slug)}
            title={p.label}
            aria-pressed={isOn}
            /* 📱 Below `md` (mobile batch item 13): a 44px CHIP with the label
               beside the emoji. The 28px emoji-only button was named only by a
               hover tooltip, and two items share 😷 — on a phone the visible
               text is what tells P3 RPE from the half-mask. Desktop unchanged:
               the label stays `sr-only` there. */
            className={`inline-flex items-center justify-center w-7 h-7 rounded text-base transition-all max-md:w-auto max-md:h-auto max-md:min-h-[44px] max-md:px-2 max-md:gap-1.5 max-md:justify-start ${
              isOn
                ? 'bg-indigo-100 dark:bg-indigo-900/40 ring-1 ring-indigo-400 dark:ring-indigo-500 opacity-100'
                : 'bg-transparent ring-1 ring-transparent hover:ring-gray-300 dark:hover:ring-gray-600 opacity-40 hover:opacity-80'
            } ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <span aria-hidden="true">{p.emoji}</span>
            <span className="sr-only max-md:not-sr-only max-md:text-xs max-md:text-left max-md:leading-tight" data-ppe-label="">{p.label}</span>
          </button>
        );
      })}
    </div>
  );
}
